import { IssueCreate } from "@boark/shared/types/issue-create";
import { IssueLinkCreate } from "@boark/shared/types/issue-link-create-or-update";
import { IssueUpdate } from "@boark/shared/types/issue-update";
import {
  IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters,
  issueWithCreatedByAndLinksAndChildrenAndParentAndRegistersSchema,
} from "@boark/shared/types/issue-with-created-by-and-links-and-children-and-parent-and-registers";
import * as z from "zod";

import {
  deleteRequest,
  getRequest,
  postRequest,
  putRequest,
} from "../utils/request";

const API_BASE_URL = process.env.API_BASE_URL || "/api";

/** Trim, drop empties, dedupe case-insensitively (keeps first spelling). */
export function normalizeIssueFormTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const raw of tags) {
    const t = raw.trim();
    if (t === "") {
      continue;
    }
    const key = t.toLowerCase();
    if (seen.has(key)) {
      continue;
    }
    seen.add(key);
    result.push(t);
  }
  return result;
}

export function uniqueSortedTagSuggestions(
  issues: { tags: string[] }[] | undefined,
): string[] {
  if (!issues?.length) {
    return [];
  }
  const unique = new Set<string>();
  for (const issue of issues) {
    for (const tag of issue.tags) {
      const trimmed = tag.trim();
      if (trimmed !== "") {
        unique.add(trimmed);
      }
    }
  }
  return [...unique].sort((left, right) => left.localeCompare(right));
}

export async function getIssues(): Promise<
  IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters[]
> {
  return getRequest(
    `${API_BASE_URL}/issue`,
    z.array(issueWithCreatedByAndLinksAndChildrenAndParentAndRegistersSchema),
  );
}

export async function getIssue(
  identifier: string | number,
): Promise<IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters> {
  return getRequest(
    `${API_BASE_URL}/issue/${identifier}`,
    issueWithCreatedByAndLinksAndChildrenAndParentAndRegistersSchema,
  );
}

export async function createIssue(
  issue: IssueCreate,
): Promise<IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters> {
  return postRequest(
    `${API_BASE_URL}/issue`,
    issue,
    issueWithCreatedByAndLinksAndChildrenAndParentAndRegistersSchema,
  );
}

export async function updateIssue(
  identifier: string | number,
  issue: IssueUpdate,
): Promise<IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters> {
  return putRequest(
    `${API_BASE_URL}/issue/${identifier}`,
    issue,
    issueWithCreatedByAndLinksAndChildrenAndParentAndRegistersSchema,
  );
}

export async function addIssueLink(
  identifier: string | number,
  link: IssueLinkCreate,
): Promise<IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters> {
  return postRequest(
    `${API_BASE_URL}/issue/${identifier}/link`,
    link,
    issueWithCreatedByAndLinksAndChildrenAndParentAndRegistersSchema,
  );
}

export async function removeIssueLink(
  identifier: string | number,
  linkId: string,
): Promise<IssueWithCreatedByAndLinksAndChildrenAndParentAndRegisters> {
  return deleteRequest(
    `${API_BASE_URL}/issue/${identifier}/link/${linkId}`,
    issueWithCreatedByAndLinksAndChildrenAndParentAndRegistersSchema,
  );
}

export async function deleteIssue(identifier: string | number): Promise<void> {
  return deleteRequest(`${API_BASE_URL}/issue/${identifier}`);
}
